/**
 * Outbox sync engine — FWA-PWA-029.
 *
 * Procesa items pending uno-por-uno con backoff. Network errors + 5xx retryable.
 * 4xx non-retryable. 401 → clearAll + logout. 412 → marca conflict + failed.
 */
import { env } from '../../env.js';
import { clearAll, deleteOutboxItem, findItemsReadyForSync, updateOutboxItem } from './db.js';
import { MAX_ATTEMPTS, type OutboxRecord, computeBackoffMs } from './types.js';

export interface SyncResult {
  processed: number;
  succeeded: number;
  retryScheduled: number;
  failedTerminal: number;
  conflicts: number;
}

interface ReplayOutcome {
  ok: boolean;
  status: number;
  errorText: string | null;
}

async function replayItem(record: OutboxRecord): Promise<ReplayOutcome> {
  try {
    const init: RequestInit = {
      method: record.method,
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'X-SISREP-Distrito': env.DISTRITO_ID,
        ...record.headers,
      },
    };
    if (record.method !== 'DELETE') {
      init.body = JSON.stringify(record.payload);
    }
    const res = await fetch(`${env.WORKER_URL}${record.endpoint}`, init);
    if (res.ok) {
      return { ok: true, status: res.status, errorText: null };
    }
    let errorText: string | null = null;
    try {
      errorText = await res.text();
    } catch {
      errorText = null;
    }
    return { ok: false, status: res.status, errorText: errorText?.slice(0, 200) ?? null };
  } catch (err) {
    return {
      ok: false,
      status: 0,
      errorText: err instanceof Error ? err.message : 'network error',
    };
  }
}

function isRetryableStatus(status: number): boolean {
  if (status === 0) return true; // network error
  if (status >= 500 && status < 600) return true;
  if (status === 429) return true;
  return false;
}

/**
 * Sync engine principal — procesa items ready for sync.
 * Llamar manualmente o desde useOutbox cuando online o post-mutation.
 */
export async function syncOutbox(opts?: {
  onAuthInvalidated?: () => void;
}): Promise<SyncResult> {
  const result: SyncResult = {
    processed: 0,
    succeeded: 0,
    retryScheduled: 0,
    failedTerminal: 0,
    conflicts: 0,
  };
  const items = await findItemsReadyForSync(new Date().toISOString());
  for (const item of items) {
    result.processed += 1;
    await updateOutboxItem(item.id, { status: 'syncing' });
    const outcome = await replayItem(item);

    if (outcome.ok) {
      await deleteOutboxItem(item.id);
      result.succeeded += 1;
      continue;
    }

    // 401 → auth invalidated; clear all + signal caller
    if (outcome.status === 401) {
      await clearAll();
      opts?.onAuthInvalidated?.();
      return result;
    }

    // 412 PreconditionFailed → conflict, marca failed con flag
    if (outcome.status === 412) {
      await updateOutboxItem(item.id, {
        status: 'failed',
        attempts: item.attempts + 1,
        last_error: '412 PreconditionFailed (server state conflict)',
        conflict: true,
        next_retry_at: null,
      });
      result.conflicts += 1;
      continue;
    }

    // Non-retryable 4xx → failed terminal
    if (!isRetryableStatus(outcome.status)) {
      await updateOutboxItem(item.id, {
        status: 'failed',
        attempts: item.attempts + 1,
        last_error: `${outcome.status} ${outcome.errorText ?? 'client error'}`,
        next_retry_at: null,
      });
      result.failedTerminal += 1;
      continue;
    }

    // Retryable: incrementa attempts + schedule
    const nextAttempt = item.attempts + 1;
    if (nextAttempt >= MAX_ATTEMPTS) {
      await updateOutboxItem(item.id, {
        status: 'failed',
        attempts: nextAttempt,
        last_error: `${outcome.status === 0 ? 'network' : outcome.status} (max attempts reached)`,
        next_retry_at: null,
      });
      result.failedTerminal += 1;
    } else {
      const backoffMs = computeBackoffMs(item.attempts);
      const nextRetryAt = new Date(Date.now() + backoffMs).toISOString();
      await updateOutboxItem(item.id, {
        status: 'pending',
        attempts: nextAttempt,
        last_error: `${outcome.status === 0 ? 'network' : outcome.status} (will retry in ${backoffMs}ms)`,
        next_retry_at: nextRetryAt,
      });
      result.retryScheduled += 1;
    }
  }
  return result;
}
